import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  AppFileUploader,
  AppModal,
  AppRemixIcon,
  AppTextArea,
} from "@/layout/AppComponents";
import { Formik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import styled from "styled-components";
import LazyLoad from "react-lazyload";
import ReactDOMServer from "react-dom/server";
import Translator from "../../../../layout/components/helpers/Translator";
import { axiosPath } from "../../../../routing/helpers/router";
import AppButton from "../../../../layout/components/form/AppButton";
import minions from "../../../../minions/minions";
import colors from "../../../../constants/colors";
import * as currentPageAction from "../../../../store/actions/currentPageAction";
import useIsMounted from "../../../../layout/components/hooks/useIsMounted";
import CommunicationPipelineLogin from "@/layout/screenSpecifics/Modules/CommunicationPipeline/CommunicationPipelineLogin";
import errorHandler from "@/layout/components/error/ErrorHandler";
import CommunicationPipelineModuleSidebar from "@/layout/screenSpecifics/Modules/CommunicationPipeline/CommunicationPipelineModuleSidebar";

export default function CommunicationPipelineUserInquiryCommunicationScreen() {
  const { moduleBridgeHex, inquiryHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const [userType] = useState('user');
  const [inquiry, setInquiry] = useState();
  const [communications, setCommunications] = useState();
  const [selectedCommunication, setSelectedCommunication] = useState();
  const [showAddModal, setShowAddModal] = useState(false);
  const [validationSchema] = useState(
    Yup.object().shape({
      message: Yup.string().required(
        ReactDOMServer.renderToString(<Translator file="module-communicationPipeline" name="required" />),
      ),
    }),
  );
  const authReports = useSelector((state) => state.currentPage?.reports) || [];
  const [isAuth, setIsAuth] = useState(false);
  const moduleData = useSelector((state) => state.currentPage?.moduleData);

  const loadCommunications = useCallback(async () => {
    axios
      .post(
        axiosPath(
          'api_r_user_module_communicationPipeline_inquiry_communication_list',
        ),
        {
          moduleBridgeHex,
          inquiryHex,
          type: userType,
        },
      )
      .then((r) => {
        if (isMounted.current && r?.data?.communications) {
          setInquiry(r?.data?.inquiry);
          setCommunications(r?.data?.communications);
          dispatch(
            currentPageAction.change(
              'unseenCommunications',
              r?.data?.unseenCommunications,
              true,
            ),
          );
        }
      })
      .catch((e) => dispatch(errorHandler(e)));
  }, [moduleBridgeHex, inquiryHex, userType]);

  useEffect(() => {
    loadCommunications();
  }, []);

  const handleAdd = useCallback(
    async (payload) => {
      axios
        .post(
          axiosPath(
            'api_r_user_module_communicationPipeline_inquiry_communication_add',
          ),
          minions.forms.toFormData({
            ...payload,
            moduleBridgeHex,
            inquiryHex,
            type: userType,
          }),
          { headers: { 'Content-Type': 'multipart/form-data' } },
        )
        .then((r) => {
          if (isMounted.current && r?.data?.communication) {
            setCommunications([r?.data?.communication, ...communications]);
            setShowAddModal(false);
          }
        })
        .catch((e) => dispatch(errorHandler(e)));
    },
    [moduleBridgeHex, inquiryHex, userType, communications],
  );

  useEffect(() => {
    if (inquiryHex && authReports.includes(inquiryHex)) {
      setIsAuth(true);
    }
  }, []);

  const handleGetFile = useCallback(
    async (salt) => {
      axios
        .post(
          axiosPath(
            'api_r_user_module_communicationPipeline_admin_report_evidence_file_get',
          ),
          {
            moduleBridgeHex,
            inquiryHex,
            communicationHex: selectedCommunication.hex,
            salt,
          },
        )
        .then(({ data }) => {
          window.open(data, '_blank', 'noopener,noreferrer');
        })
        .catch((e) => errorHandler(e));
    },
    [moduleBridgeHex, inquiryHex, selectedCommunication],
  );
  // href={`${global.urlData.userApiUrl}m/communication-pipeline/inquiry/communication/get-file/${moduleBridgeHex}/${inquiryHex}/${selectedCommunication.hex}/${salt}`}

  if (isAuth === false) {
    return (
      <>
        {moduleData?.permissionLevel === 0 && (
          <CommunicationPipelineModuleSidebar />
        )}
        <AppModal show={isAuth === false} isCloseButton={false}>
          <CommunicationPipelineLogin onAuth={() => setIsAuth(true)} />
        </AppModal>
      </>
    );
  }

  return (
    <>
      {moduleData?.permissionLevel === 0 && (
        <CommunicationPipelineModuleSidebar />
      )}
      <Container>
        <h3 className="mb-3">
          <span className="badge badge-dark mr-2">{inquiry?.dateStamp}</span>
          {inquiry?.name}
        </h3>
        <AppButton
          color="success"
          className="float-right mb-3"
          icon="add"
          onClick={() => setShowAddModal(true)}
        >
          <Translator
            file="module-communicationPipeline"
            name="add-communication"
          />
        </AppButton>
        <div className="clearfix" />
        <CommunicationWrapper className="mb-3">
          {communications?.map((c) => (
            <LazyLoad key={c?.hex}>
              <CommunicationCard
                $type={c?.type}
                $userType={userType}
                className="pointer mb-3"
                onClick={() => setSelectedCommunication(c)}
              >
                <div className="corner" />
                <div>
                  <small>
                    <i>
                      <Translator
                        file="module-communicationPipeline"
                        name={c?.type === 'user' ? 'user' : 'administrator'}
                      />
                    </i>
                  </small>
                  {c?.files && Object.keys(c?.files).length > 0 && (
                    <span className="float-right">
                      <AppRemixIcon icon="attachment" />
                    </span>
                  )}
                </div>
                <div>{c?.message}</div>
                <div className="float-right">
                  <small>{c?.dateStamp}</small>
                </div>
              </CommunicationCard>
              <div className="clearfix" />
            </LazyLoad>
          ))}
          {communications !== undefined && communications?.length === 0 && (
            <EmptyCommunications>
              <AppRemixIcon icon="question-answer" size={40} />
              <div>
                <Translator
                  file="module-communicationPipeline"
                  name="no-communications-exist"
                />
              </div>
            </EmptyCommunications>
          )}
        </CommunicationWrapper>
        <AppModal
          title={
            selectedCommunication?.hex
              ? `${(
                  <Translator
                    file="module-communicationPipeline"
                    name="communication"
                  />
                )} - ${selectedCommunication?.hex}`
              : ''
          }
          show={!!selectedCommunication}
          onClose={() => setSelectedCommunication(undefined)}
          onConfirm={() => setSelectedCommunication(undefined)}
          confirmBtnText={
            <Translator file="module-communicationPipeline" name="close" />
          }
          isConfirmBtn
        >
          <AppTextArea
            title={ReactDOMServer.renderToString(
              <Translator file="module-communicationPipeline" name="message" />,
            )}
            value={selectedCommunication?.message}
            resize="vertical"
            height="180px"
            isReadOnly
          />
          {selectedCommunication?.files &&
            Object.keys(selectedCommunication?.files).length > 0 && (
              <>
                <h6>
                  <Translator
                    file="module-communicationPipeline"
                    name="documents"
                  />
                </h6>
                <div className="table-responsive">
                  <table className="table table-hover table-sm">
                    <tbody>
                      {Object.keys(selectedCommunication.files).map((salt) => (
                        <tr key={salt}>
                          <td>
                            <span
                            onClick={() => handleGetFile(salt)}
                              // href={`${global.urlData.userApiUrl}m/communication-pipeline/inquiry/communication/get-file/${moduleBridgeHex}/${inquiryHex}/${selectedCommunication.hex}/${salt}`}
                              title={selectedCommunication?.files?.[salt]?.name}
                              className="pointer"
                            >
                              <AppRemixIcon icon="file-text" />{' '}
                              {selectedCommunication?.files?.[salt]?.name}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
        </AppModal>
        <AppModal
          title={ReactDOMServer.renderToString(
            <Translator
              file="module-communicationPipeline"
              name="add-communication"
            />,
          )}
          show={showAddModal}
          onClose={() => setShowAddModal(false)}
        >
          <Formik
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={validationSchema}
            initialValues={{
              message: '',
              files: [],
              type: userType,
            }}
            onSubmit={handleAdd}
          >
            {({
              setFieldValue,
              handleSubmit,
              values,
              errors,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <AppTextArea
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-communicationPipeline"
                      name="message"
                    />,
                  )}
                  value={values?.message}
                  error={errors?.message}
                  onChange={(v) => setFieldValue('message', v)}
                  resize="vertical"
                  height="180px"
                />
                <AppFileUploader
                  onChange={(v) => setFieldValue('files', v)}
                  value={values.files}
                  placeholder={
                    values?.files?.length === 0 ? (
                      <Translator
                        file="module-communicationPipeline"
                        name="select-one-more-files"
                      />
                    ) : (
                      <Translator
                        file="module-communicationPipeline"
                        name="elements-selected-elements"
                        variables={{ elements: values?.files.length }}
                      />
                    )
                  }
                  isMulti
                />
                <button
                  type="submit"
                  className="btn btn-success btn-sm float-right"
                  disabled={isSubmitting}
                >
                  <Translator file="module-communicationPipeline" name="add" />
                </button>
              </form>
            )}
          </Formik>
        </AppModal>
      </Container>
    </>
  );
}

const Container = styled.div`
  padding-left: 0.5rem !important;
  h3 {
    font-size: 16px;
    font-weight: 700;
    margin: 32px 0 0 0;
  }
  .minHeight600 {
    min-height: 600px;
  }
  .user-info {
    border: 1px solid lightgray;
    padding: 24px;
  }
`;

const CommunicationWrapper = styled.div`
  height: calc(100vh - 250px);
  border: 1px solid #ddd;
  overflow-y: scroll;
  padding: 25px;
  background-color: #fff;
  h3 {
    font-size: 14px;
    font-weight: 700;
    margin: 32px 0 0 0;
  }
  .minHeight600 {
    min-height: 600px;
  }
  .user-info {
    border: 1px solid lightgray;
    padding: 24px;
  }
`;

const EmptyCommunications = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: ${colors.grey500};
  font-size: 20px;
`;

const CommunicationCard = styled.div`
  padding: 12px;
  background-color: ${(props) =>
    props.$type === props.$userType ? colors.info : colors.grey200};
  position: relative;
  float: ${(props) => (props.$type === props.$userType ? "right" : "left")};
  h3 {
    font-size: 14px;
    font-weight: 700;
    margin: 32px 0 0 0;
  }
  .corner {
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-top: 10px solid
      ${(props) =>
        props.$type === props.$userType ? colors.info : colors.grey200};
    position: absolute;
    top: 0px;
    ${(props) =>
      props.$type === props.$userType ? "right: -9px;" : "left: -9px;"};
  }
`;
